import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FiUsers, FiFolder, FiFileText, FiLayers, FiHardDrive,
  FiChevronRight, FiArrowLeft, FiFile, FiExternalLink,
} from 'react-icons/fi';
import { loadTable, getTableData, saveRow, addRow } from '../db-viewer/data/dataAccess';
import { migrateDocsToBlob, MigrateProgress } from '../bookkeeping/utils/docStorage';
import { isIdbKey } from '../../lib/docStore';
import './DmsReportsTab.css';

// =============================================
// DMS → Masters → Folders → Reports
// A clickable dashboard over the document store: headline stats, capacity meters,
// and a per-owner breakdown that drills owner → folders → documents. The final
// clicks hand off to the live tabs: a folder name opens the Folder List tab; a
// document opens Process Docs / Receipts. (Navigation handled by the parent.)
// =============================================

interface DmsReportsTabProps {
  onOpenFolder: (folderId: number) => void;
  onOpenDocument: (docId: number) => void;
}

const fmtBytes = (b: number): string => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const fmtNum = (n: number): string => (n || 0).toLocaleString();

interface OwnerAgg { key: string; name: string; kind: string; docs: number; pages: number; bytes: number; }

const DmsReportsTab: React.FC<DmsReportsTabProps> = ({ onOpenFolder, onOpenDocument }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [folders, setFolders] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);

  const [drillOwner, setDrillOwner] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [assignMsg, setAssignMsg] = useState('');
  // Bulk-assign in the Unassigned drill: which docs are checked, and which owner(s)
  // to assign them to (multi — primary goes on dms_documents, extras to dms_document_owners).
  const [selectedDocIds, setSelectedDocIds] = useState<Set<number>>(new Set());
  const [assignOwnerKeys, setAssignOwnerKeys] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);
  // Blob-storage migration of legacy (base64-only) docs
  const [migrating, setMigrating] = useState(false);
  const [migProgress, setMigProgress] = useState<MigrateProgress | null>(null);
  const [migDoneMsg, setMigDoneMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, b, f, d, p] = await Promise.all([
          loadTable('users'), loadTable('user_businesses'),
          loadTable('dms_folders'), loadTable('dms_documents'),
          loadTable('site_service_plans'),
        ]);
        if (cancelled) return;
        setUsers(u); setBusinesses(b); setFolders(f);
        setDocuments(d.map((x: any) => { const { file_base64, ...rest } = x; return rest; }));
        setPlans(p); setLoaded(true);
      } catch {
        if (cancelled) return;
        setUsers(getTableData('users')); setBusinesses(getTableData('user_businesses'));
        setFolders(getTableData('dms_folders'));
        setDocuments(getTableData('dms_documents', { omit: ['file_base64'] }));
        setPlans(getTableData('site_service_plans')); setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // eslint-disable-next-line eqeqeq
  const activeDocs = useMemo(() => documents.filter(d => d.is_deleted != 1 && d.is_active != 0), [documents]);

  // Docs still read from the DB (no idb:// blob pointer) — candidates to migrate.
  const pendingMigration = useMemo(
    () => activeDocs.filter(d => !isIdbKey(d.file_path)).map(d => Number(d.document_id)),
    [activeDocs]
  );

  const runBlobMigration = useCallback(async () => {
    if (migrating || pendingMigration.length === 0) return;
    setMigrating(true);
    setMigDoneMsg('');
    setMigProgress({ done: 0, total: pendingMigration.length, migrated: 0, skipped: 0, failed: 0 });
    const res = await migrateDocsToBlob(pendingMigration, p => setMigProgress({ ...p }));
    setMigrating(false);
    setMigDoneMsg(`Migrated ${res.migrated} document${res.migrated !== 1 ? 's' : ''} to blob storage${res.failed ? ` · ${res.failed} failed` : ''}.`);
    // Refresh metadata so file_path / counts update.
    try {
      const d = await loadTable('dms_documents');
      setDocuments(d.map((x: any) => { const { file_base64, ...rest } = x; return rest; }));
    } catch { /* keep current */ }
  }, [migrating, pendingMigration]);
  // eslint-disable-next-line eqeqeq
  const activeFolders = useMemo(() => folders.filter(f => f.is_deleted != 1 && f.is_active != 0), [folders]);
  // eslint-disable-next-line eqeqeq
  const activeUsers = useMemo(() => users.filter(u => u.is_deleted != 1), [users]);

  // Resolve the "owner" a document is filed under (business / person), with a
  // stable grouping key.
  const resolveOwner = useCallback((d: any): { key: string; name: string; kind: string } => {
    const ot = d.owner_type; const oid = d.owner_id;
    if (ot === 'business' || ot === 'client_business') {
      // eslint-disable-next-line eqeqeq
      const b = businesses.find((x: any) => x.business_id == oid);
      return { key: `biz:${oid}`, name: b ? (b.busi_legal_name || b.busi_operating_name || 'Business') : 'Business', kind: 'Business' };
    }
    if (oid && (ot === 'account_owner' || ot === 'family_member' || ot === 'client_personal')) {
      // eslint-disable-next-line eqeqeq
      const u = users.find((x: any) => x.user_id == oid);
      return { key: `user:${oid}`, name: u ? (`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'User') : 'User', kind: ot === 'account_owner' ? 'Owner' : 'Member' };
    }
    // No owner chosen at upload (owner_type/owner_id null) → group as Unassigned.
    // Do NOT fall back to the uploader (user_id) — the uploader is not the owner.
    return { key: 'unassigned', name: 'Unassigned (no owner)', kind: 'No owner' };
  }, [users, businesses]);

  const totals = useMemo(() => {
    let pages = 0; let bytes = 0;
    activeDocs.forEach(d => { pages += Number(d.page_count) || 0; bytes += Number(d.file_size_original) || 0; });
    return { users: activeUsers.length, folders: activeFolders.length, docs: activeDocs.length, pages, bytes };
  }, [activeDocs, activeUsers, activeFolders]);

  const owners = useMemo((): OwnerAgg[] => {
    const m = new Map<string, OwnerAgg>();
    activeDocs.forEach(d => {
      const o = resolveOwner(d);
      let e = m.get(o.key);
      if (!e) { e = { key: o.key, name: o.name, kind: o.kind, docs: 0, pages: 0, bytes: 0 }; m.set(o.key, e); }
      e.docs++; e.pages += Number(d.page_count) || 0; e.bytes += Number(d.file_size_original) || 0;
    });
    return Array.from(m.values()).sort((a, b) => b.docs - a.docs);
  }, [activeDocs, resolveOwner]);

  const maxOwnerDocs = owners.reduce((m, o) => Math.max(m, o.docs), 0) || 1;

  // ---- Inline "assign owner" for the Unassigned drill ----
  // Owners you can assign to: the account owner, their family, and their businesses
  // (clients live under Client's Accounts, not DMS).
  const assignOwnerOptions = useMemo(() => {
    const nameOf = (u: any) => (`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'User');
    // eslint-disable-next-line eqeqeq
    const ao = users.find((u: any) => u.owner_id == null && u.is_deleted !== 1);
    const aid = ao ? Number(ao.user_id) : null;
    const opts: { value: string; ot: string; oid: number; label: string }[] = [];
    if (ao) opts.push({ value: `account_owner:${ao.user_id}`, ot: 'account_owner', oid: Number(ao.user_id), label: `${nameOf(ao)} · Owner` });
    users
      // eslint-disable-next-line eqeqeq
      .filter((u: any) => u.is_deleted !== 1 && u.owner_id != null && Number(u.owner_id) === aid)
      .sort((a: any, b: any) => nameOf(a).localeCompare(nameOf(b)))
      .forEach((u: any) => opts.push({ value: `family_member:${u.user_id}`, ot: 'family_member', oid: Number(u.user_id), label: `${nameOf(u)} · Family` }));
    businesses
      // eslint-disable-next-line eqeqeq
      .filter((b: any) => b.is_deleted !== 1 && Number(b.owner_id) === aid && Number(b.is_client_business) !== 1)
      .sort((a: any, b: any) => (a.busi_legal_name || '').localeCompare(b.busi_legal_name || ''))
      .forEach((b: any) => opts.push({ value: `business:${b.business_id}`, ot: 'business', oid: Number(b.business_id), label: `${b.busi_legal_name || b.busi_operating_name || 'Business'} · Business` }));
    return opts;
  }, [users, businesses]);

  const toggleDoc = useCallback((id: number) => {
    setSelectedDocIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const toggleOwnerKey = useCallback((k: string) => {
    setAssignOwnerKeys(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  }, []);

  // Assign every checked doc to the chosen owner(s): the first owner becomes the
  // primary (on dms_documents → moves the doc out of Unassigned); any extras are
  // added as dms_document_owners rows (best-effort), mirroring multi-owner filing.
  const assignSelected = useCallback(async () => {
    const chosen = assignOwnerKeys.map(k => assignOwnerOptions.find(o => o.value === k)).filter(Boolean) as { ot: string; oid: number }[];
    const ids = Array.from(selectedDocIds);
    if (!chosen.length || !ids.length || assigning) return;
    const primary = chosen[0]; const extras = chosen.slice(1);
    const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
    setAssigning(true);
    try {
      for (const docId of ids) {
        await saveRow('dms_documents', docId, { owner_type: primary.ot, owner_id: primary.oid, updated_at: now }, 'document_id');
        for (const ex of extras) {
          try {
            await addRow('dms_document_owners', { document_id: docId, owner_type: ex.ot, owner_id: ex.oid, is_primary: 0, folder_id: null, is_active: 1, is_deleted: 0 }, 'doc_owner_id');
          } catch { /* extra owner is best-effort */ }
        }
      }
      setDocuments(prev => prev.map(d => selectedDocIds.has(d.document_id) ? { ...d, owner_type: primary.ot, owner_id: primary.oid, updated_at: now } : d));
      setAssignMsg(`Assigned ${ids.length} document${ids.length !== 1 ? 's' : ''} to ${chosen.length} owner${chosen.length !== 1 ? 's' : ''}.`);
      setSelectedDocIds(new Set());
      setAssignOwnerKeys([]);
      window.setTimeout(() => setAssignMsg(''), 2500);
    } catch {
      setAssignMsg('Could not assign — please try again.');
      window.setTimeout(() => setAssignMsg(''), 2500);
    } finally {
      setAssigning(false);
    }
  }, [assignOwnerKeys, assignOwnerOptions, selectedDocIds, assigning]);

  // Storage meter against the account owner's plan cap (real); falls back to 1 GB.
  const storageCapMB = useMemo(() => {
    // eslint-disable-next-line eqeqeq
    const ao = users.find((u: any) => u.owner_id == null || u.owner_id === '') || users[0];
    // eslint-disable-next-line eqeqeq
    const plan = ao ? plans.find((p: any) => p.plan_id == ao.plan_id) : null;
    const cap = plan ? Number(plan.max_storage_mb) : 0;
    return cap > 0 ? cap : 1024;
  }, [users, plans]);
  const usedMB = totals.bytes / (1024 * 1024);
  const storagePct = Math.min(100, storageCapMB > 0 ? (usedMB / storageCapMB) * 100 : 0);

  // Pages meter: soft headroom cap so the bar reads as a gauge with room to grow.
  const pagesCap = Math.max(50, Math.ceil((totals.pages * 1.4) / 50) * 50);
  const pagesPct = Math.min(100, (totals.pages / pagesCap) * 100);

  // ---- Drill data (owner → folders → documents) ----
  const drillOwnerObj = owners.find(o => o.key === drillOwner) || null;
  const ownerDocs = useMemo(
    () => (drillOwner ? activeDocs.filter(d => resolveOwner(d).key === drillOwner) : []),
    [drillOwner, activeDocs, resolveOwner]
  );
  const ownerFolders = useMemo(() => {
    const m = new Map<string, { id: number | null; name: string; docs: number }>();
    ownerDocs.forEach(d => {
      const fid = d.dms_folders_id != null ? Number(d.dms_folders_id) : null;
      const key = fid == null ? 'unfiled' : String(fid);
      let e = m.get(key);
      if (!e) {
        const f = fid != null ? activeFolders.find((x: any) => x.folder_id === fid) : null;
        e = { id: fid, name: f ? f.folder_name : 'Unfiled documents', docs: 0 };
        m.set(key, e);
      }
      e.docs++;
    });
    return Array.from(m.values()).sort((a, b) => b.docs - a.docs);
  }, [ownerDocs, activeFolders]);
  const folderDocs = useCallback(
    (fid: number | null) => ownerDocs.filter(d => (d.dms_folders_id != null ? Number(d.dms_folders_id) : null) === fid),
    [ownerDocs]
  );

  const toggleFolder = (key: string) => setExpandedFolders(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const enterOwner = (key: string) => { setDrillOwner(key); setExpandedFolders(new Set()); setSelectedDocIds(new Set()); setAssignOwnerKeys([]); };
  const backToOverview = () => { setDrillOwner(null); setExpandedFolders(new Set()); setSelectedDocIds(new Set()); setAssignOwnerKeys([]); };

  if (!loaded) {
    return <div className="dr-loading">Loading dashboard…</div>;
  }

  const STAT_CARDS = [
    { icon: <FiUsers />, label: 'Users', value: fmtNum(totals.users), cls: 'users' },
    { icon: <FiFolder />, label: 'Folders', value: fmtNum(totals.folders), cls: 'folders' },
    { icon: <FiFileText />, label: 'Documents', value: fmtNum(totals.docs), cls: 'docs' },
    { icon: <FiLayers />, label: 'Pages', value: fmtNum(totals.pages), cls: 'pages' },
    { icon: <FiHardDrive />, label: 'Storage', value: fmtBytes(totals.bytes), cls: 'storage' },
  ];

  return (
    <div className="dr-dashboard">
      {/* ===== Headline stat cards ===== */}
      <div className="dr-stats">
        {STAT_CARDS.map(s => (
          <div key={s.label} className={`dr-stat-card ${s.cls}`}>
            <div className="dr-stat-icon">{s.icon}</div>
            <div className="dr-stat-text">
              <div className="dr-stat-value">{s.value}</div>
              <div className="dr-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ===== Capacity meters ===== */}
      <div className="dr-meters">
        <div className="dr-meter">
          <div className="dr-meter-head">
            <span className="dr-meter-label"><FiHardDrive size={13} /> Storage used</span>
            <span className="dr-meter-val">{fmtBytes(totals.bytes)} <span className="dr-meter-cap">/ {fmtNum(storageCapMB)} MB</span></span>
          </div>
          <div className="dr-meter-track">
            <div className={`dr-meter-fill storage ${storagePct > 85 ? 'hot' : ''}`} style={{ width: `${storagePct}%` }} />
          </div>
          <div className="dr-meter-pct">{storagePct.toFixed(storagePct < 10 ? 1 : 0)}% of plan capacity</div>
        </div>
        <div className="dr-meter">
          <div className="dr-meter-head">
            <span className="dr-meter-label"><FiLayers size={13} /> Pages across all documents</span>
            <span className="dr-meter-val">{fmtNum(totals.pages)} <span className="dr-meter-cap">pages</span></span>
          </div>
          <div className="dr-meter-track">
            <div className="dr-meter-fill pages" style={{ width: `${pagesPct}%` }} />
          </div>
          <div className="dr-meter-pct">{totals.docs ? (totals.pages / totals.docs).toFixed(1) : '0'} pages per document avg</div>
        </div>
      </div>

      {/* ===== Blob storage ===== */}
      <div className="dr-blobstore">
        <div className="dr-meter-head">
          <span className="dr-meter-label"><FiHardDrive size={13} /> Blob storage</span>
          <span className="dr-meter-val">
            {activeDocs.length - pendingMigration.length} <span className="dr-meter-cap">/ {activeDocs.length} in blob store</span>
          </span>
        </div>
        <p className="dr-blobstore-note">
          Documents are stored as blobs out of the database row, addressed by <code>file_path</code> —
          IndexedDB locally, Cloud Storage on live. {pendingMigration.length > 0
            ? `${pendingMigration.length} legacy document${pendingMigration.length !== 1 ? 's' : ''} still read${pendingMigration.length === 1 ? 's' : ''} from the database.`
            : 'All documents are in the blob store.'}
        </p>
        {pendingMigration.length > 0 && (
          <button className="dr-blobstore-btn" onClick={runBlobMigration} disabled={migrating}>
            {migrating
              ? `Migrating… ${migProgress?.done ?? 0}/${migProgress?.total ?? 0}`
              : `Migrate ${pendingMigration.length} document${pendingMigration.length !== 1 ? 's' : ''} to blob storage`}
          </button>
        )}
        {migDoneMsg && <div className="dr-blobstore-done">{migDoneMsg}</div>}
      </div>

      {/* ===== Per-owner analysis (drill-down) ===== */}
      {!drillOwner ? (
        <div className="dr-section">
          <div className="dr-section-title">Documents by owner <span className="dr-section-hint">click an owner to drill in →</span></div>
          {owners.length === 0 ? (
            <div className="dr-empty">No documents yet.</div>
          ) : (
            <div className="dr-bars">
              {owners.map(o => (
                <button key={o.key} className="dr-bar-row" onClick={() => enterOwner(o.key)} title={`Drill into ${o.name}`}>
                  <span className="dr-bar-name">
                    <span className={`dr-owner-kind ${o.kind.toLowerCase()}`}>{o.kind}</span>
                    {o.name}
                  </span>
                  <span className="dr-bar-track">
                    <span className="dr-bar-fill" style={{ width: `${(o.docs / maxOwnerDocs) * 100}%` }} />
                  </span>
                  <span className="dr-bar-meta">
                    <b>{fmtNum(o.docs)}</b> docs · {fmtNum(o.pages)} pg · {fmtBytes(o.bytes)}
                  </span>
                  <FiChevronRight className="dr-bar-caret" size={16} />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="dr-section">
          <div className="dr-drill-head">
            <button className="dr-back" onClick={backToOverview}><FiArrowLeft size={14} /> All owners</button>
            <div className="dr-drill-title">
              <span className={`dr-owner-kind ${(drillOwnerObj?.kind || '').toLowerCase()}`}>{drillOwnerObj?.kind}</span>
              {drillOwnerObj?.name}
            </div>
            <div className="dr-drill-summary">
              {fmtNum(drillOwnerObj?.docs || 0)} docs · {fmtNum(drillOwnerObj?.pages || 0)} pages · {fmtBytes(drillOwnerObj?.bytes || 0)}
            </div>
          </div>
          {drillOwner === 'unassigned' && (() => {
            const allIds = ownerDocs.map(d => d.document_id);
            const allChecked = allIds.length > 0 && allIds.every(id => selectedDocIds.has(id));
            return (
              <div className="dr-assign-panel">
                <div className="dr-assign-hint">
                  Check documents, choose owner(s), then Assign — they leave Unassigned right away.
                  {assignMsg && <span className="dr-assign-msg">{assignMsg}</span>}
                </div>
                <div className="dr-assign-bar">
                  <label className="dr-assign-all">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={e => setSelectedDocIds(e.target.checked ? new Set(allIds) : new Set())}
                    /> Select all ({allIds.length})
                  </label>
                  <span className="dr-assign-sel">{selectedDocIds.size} selected</span>
                  <div className="dr-assign-owners">
                    {assignOwnerOptions.map(o => (
                      <label key={o.value} className={`dr-assign-chip ${assignOwnerKeys.includes(o.value) ? 'on' : ''}`}>
                        <input type="checkbox" checked={assignOwnerKeys.includes(o.value)} onChange={() => toggleOwnerKey(o.value)} /> {o.label}
                      </label>
                    ))}
                  </div>
                  <button
                    className="dr-assign-btn"
                    disabled={selectedDocIds.size === 0 || assignOwnerKeys.length === 0 || assigning}
                    onClick={assignSelected}
                  >
                    {assigning ? 'Assigning…' : `Assign ${selectedDocIds.size || ''} → ${assignOwnerKeys.length || ''} owner${assignOwnerKeys.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            );
          })()}
          <div className="dr-folders">
            {ownerFolders.map(f => {
              const fkey = f.id == null ? 'unfiled' : String(f.id);
              const open = expandedFolders.has(fkey);
              return (
                <div key={fkey} className="dr-folder-block">
                  <div className="dr-folder-row">
                    <button className="dr-folder-expand" onClick={() => toggleFolder(fkey)} title={open ? 'Collapse' : 'Expand'}>
                      <FiChevronRight className={open ? 'open' : ''} size={15} />
                    </button>
                    <FiFolder size={14} className="dr-folder-ico" />
                    {f.id != null ? (
                      <button className="dr-folder-name link" onClick={() => onOpenFolder(f.id as number)} title="Open in Folder List">
                        {f.name} <FiExternalLink size={12} />
                      </button>
                    ) : (
                      <span className="dr-folder-name">{f.name}</span>
                    )}
                    <span className="dr-folder-count">{fmtNum(f.docs)} docs</span>
                  </div>
                  {open && (
                    <div className="dr-doc-list">
                      {folderDocs(f.id).map(d => {
                        const nm = d.filename || d.original_filename || `Document #${d.document_id}`;
                        const meta = `${fmtNum(Number(d.page_count) || 0)} pg · ${fmtBytes(Number(d.file_size_original) || 0)}`;
                        if (drillOwner === 'unassigned') {
                          const checked = selectedDocIds.has(d.document_id);
                          return (
                            <label key={d.document_id} className={`dr-doc-row dr-doc-row-assign ${checked ? 'checked' : ''}`}>
                              <input type="checkbox" className="dr-doc-check" checked={checked} onChange={() => toggleDoc(d.document_id)} />
                              <FiFile size={13} className="dr-doc-ico" />
                              <span className="dr-doc-name link" onClick={(e) => { e.preventDefault(); onOpenDocument(d.document_id); }} title="Open in Process Docs / Receipts">{nm}</span>
                              <span className="dr-doc-meta">{meta}</span>
                            </label>
                          );
                        }
                        return (
                          <button
                            key={d.document_id}
                            className="dr-doc-row"
                            onClick={() => onOpenDocument(d.document_id)}
                            title="Open in Process Docs / Receipts"
                          >
                            <FiFile size={13} className="dr-doc-ico" />
                            <span className="dr-doc-name">{nm}</span>
                            <span className="dr-doc-meta">{meta}</span>
                            <FiExternalLink size={12} className="dr-doc-open" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DmsReportsTab;
